<template>
	<section>
		<loader :dialog="loaderDialog"></loader>
		<floating-alert :message="showAlertMessage" :show="showAlert" :type="showAlertColor"></floating-alert>

		<v-alert type="error" prominent rounded="xl" class="mb-4" v-if="accessDenied">
			<h5>Accès refusé</h5>
			Vous n'avez pas les autorisations pour accéder à ce contenu.
		</v-alert>

		<!-- ─── Liste des abonnements ─── -->
		<v-card elevation="2" rounded="xl">
			<div class="sub-header">
				<div class="sub-header-left">
					<div class="sub-icon-box">
						<v-icon size="18" color="white">mdi-playlist-check</v-icon>
					</div>
					<div>
						<p class="sub-title">Abonnements</p>
						<p class="sub-subtitle">Gestion des formules</p>
					</div>
				</div>

				<v-text-field
					v-model="search"
					prepend-inner-icon="mdi-magnify"
					placeholder="Rechercher..."
					outlined dense hide-details
					class="sub-search"
					color="blue darken-2"
				></v-text-field>

				<div class="sub-actions" v-if="!accessDenied">
					<v-btn depressed rounded class="sub-bulk-btn" @click="openBulk">
						<v-icon left small>mdi-account-multiple-plus</v-icon>
						Attribution groupée
					</v-btn>
					<v-btn depressed rounded class="sub-attr-btn" @click="openAttribution(null)">
						<v-icon left small>mdi-account-plus</v-icon>
						Attribuer
					</v-btn>
					<v-dialog v-model="dialog" max-width="520px">
						<template v-slot:activator="{ on, attrs }">
							<v-btn depressed rounded class="sub-add-btn" v-bind="attrs" v-on="on">
								<v-icon left small>mdi-plus</v-icon>
								Nouveau
							</v-btn>
						</template>
						<v-card rounded="xl" class="pa-2">
							<div class="sub-dialog-header">
								<div class="sub-icon-box-sm">
									<v-icon size="16" color="white">mdi-playlist-check</v-icon>
								</div>
								<div>
									<p class="sub-dialog-title">{{ formTitle }}</p>
									<p class="sub-dialog-sub">Formule d'abonnement</p>
								</div>
								<v-spacer></v-spacer>
								<v-btn icon small @click="close">
									<v-icon color="#6b7280">mdi-close</v-icon>
								</v-btn>
							</div>
							<v-divider></v-divider>
							<v-card-text class="pt-5">
								<v-row>
									<v-col cols="12" sm="6">
										<v-select :items="subscriptionTypes" label="Type" v-model="subscriptionPeriod" return-object item-text="type" outlined dense rounded hide-details></v-select>
									</v-col>
									<v-col cols="12" sm="6">
										<v-select :items="subscriptionPeriod.period || []" label="Intitulé" v-model="editedItem" return-object item-text="label" outlined dense rounded hide-details></v-select>
									</v-col>
									<v-col cols="12" sm="6">
										<v-text-field v-model="editedItem.price" label="Prix" outlined dense rounded suffix="FCFA" hide-details type="number" min="0"></v-text-field>
									</v-col>
									<v-col cols="12" sm="6">
										<v-text-field v-model="editedItem.duration" label="Durée" outlined dense rounded suffix="jours" hide-details disabled></v-text-field>
									</v-col>
								</v-row>
							</v-card-text>
							<v-card-actions class="pa-4 pt-2">
								<v-btn text color="error" rounded @click="close">Annuler</v-btn>
								<v-spacer></v-spacer>
								<v-btn depressed rounded class="sub-save-btn" @click="save">Enregistrer</v-btn>
							</v-card-actions>
						</v-card>
					</v-dialog>
				</div>
			</div>

			<v-divider></v-divider>

			<v-data-table
				:headers="headers"
				:items="subscriptions"
				:server-items-length="totalItems"
				:options.sync="options"
				class="sub-table"
				:loading="loading"
				loading-text="Chargement en cours..."
				:footer-props="{
					'items-per-page-options': [10, 25, 50],
					'items-per-page-all-text': 'Tout',
					'items-per-page-text': 'Lignes par page',
				}"
			>
				<template v-slot:[`item.price`]="{ item }">
					<span class="price-value">{{ item.price.toLocaleString('fr') }} FCFA</span>
				</template>
				<template v-slot:[`item.duration`]="{ item }">
					<span>{{ item.duration }} jours</span>
				</template>
				<template v-slot:[`item.type`]="{ item }">
					<v-chip small color="#eff6ff" text-color="#1565c0">{{ item.type }}</v-chip>
				</template>
				<template v-slot:[`item.actions`]="{ item }">
					<v-tooltip bottom>
						<template v-slot:activator="{ on, attrs }">
							<v-btn icon small v-bind="attrs" v-on="on" @click="openAttribution(item)">
								<v-icon size="17" color="#1565c0">mdi-account-plus</v-icon>
							</v-btn>
						</template>
						<span>Attribuer</span>
					</v-tooltip>
					<v-btn icon small @click="editItem(item)">
						<v-icon size="17" color="#374151">mdi-pencil</v-icon>
					</v-btn>
					<v-tooltip bottom :disabled="!item.isAttributed">
						<template v-slot:activator="{ on, attrs }">
							<span v-bind="attrs" v-on="on">
								<v-btn icon small @click="deleteItem(item)" :disabled="item.isAttributed">
									<v-icon size="17" :color="item.isAttributed ? '#d1d5db' : '#c62828'">mdi-delete</v-icon>
								</v-btn>
							</span>
						</template>
						<span>Cet abonnement est attribué à un client et ne peut pas être supprimé.</span>
					</v-tooltip>
				</template>
				<template v-slot:[`footer.page-text`]="items">
					{{ items.pageStart }}-{{ items.pageStop }} sur {{ items.itemsLength }}
				</template>
				<template v-slot:no-data>
					<v-btn text color="primary" @click="initialize">Actualiser</v-btn>
				</template>
			</v-data-table>
		</v-card>

		<!-- ─── Dialog: Attribution individuelle ─── -->
		<v-dialog v-model="dialogAttribution" max-width="480px" persistent>
			<v-card rounded="xl" class="pa-2">
				<div class="attr-header">
					<div class="attr-icon-box">
						<v-icon size="16" color="white">mdi-account-plus</v-icon>
					</div>
					<div>
						<p class="attr-title">Attribuer un abonnement</p>
						<p class="attr-sub">Attribution individuelle</p>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small @click="closeAttribution">
						<v-icon color="#6b7280">mdi-close</v-icon>
					</v-btn>
				</div>
				<v-divider></v-divider>

				<v-card-text class="pt-5">
					<v-form ref="attrForm" v-model="attrValid" lazy-validation>
						<!-- 1. Abonnement -->
						<v-select
							v-model="attrForm.subscription"
							:items="subscriptions"
							return-object
							label="Abonnement"
							outlined dense rounded
							prepend-inner-icon="mdi-playlist-check"
							:rules="[v => !!v || 'Champ requis']"
							hide-details="auto"
							class="mb-4"
						>
							<template #item="{ item }">
								{{ item.label }} — {{ item.type }} — {{ item.price.toLocaleString('fr') }} FCFA
							</template>
							<template #selection="{ item }">
								{{ item.label }} — {{ item.price.toLocaleString('fr') }} FCFA
							</template>
						</v-select>

						<!-- 2. Client avec recherche -->
						<v-autocomplete
							v-model="attrForm.client"
							:items="attrClientResults"
							:search-input.sync="attrClientSearch"
							:filter="() => true"
							item-text="fullName"
							return-object
							label="Client"
							outlined dense rounded
							prepend-inner-icon="mdi-account"
							:rules="[v => !!v || 'Champ requis']"
							hide-details="auto"
							class="mb-4"
							:loading="attrSearchLoading"
							no-data-text="Tapez un nom pour rechercher..."
							placeholder="Rechercher un client..."
						>
							<template #item="{ item }">
								<v-list-item-avatar v-if="item.isNew" size="28">
									<v-icon color="#c62828" size="20">mdi-account-plus</v-icon>
								</v-list-item-avatar>
								<v-list-item-content>
									<v-list-item-title v-if="item.isNew" style="color:#c62828;font-weight:700;">
										Créer « {{ item.fullName }} » comme nouveau client
									</v-list-item-title>
									<v-list-item-title v-else>{{ item.fullName }}</v-list-item-title>
								</v-list-item-content>
							</template>
							<template #selection="{ item }">
								<span :style="item.isNew ? 'color:#c62828;font-weight:700;' : ''">
									<v-icon v-if="item.isNew" small color="#c62828" class="mr-1">mdi-account-plus</v-icon>
									{{ item.fullName }}{{ item.isNew ? ' (nouveau)' : '' }}
								</span>
							</template>
						</v-autocomplete>

						<!-- Téléphone optionnel pour les nouveaux clients -->
						<v-text-field
							v-if="attrForm.client && attrForm.client.isNew"
							v-model="attrForm.newClientPhone"
							label="Téléphone (optionnel)"
							outlined dense rounded
							prepend-inner-icon="mdi-phone"
							hide-details="auto"
							class="mb-4"
							placeholder="Ex : 06 12 34 56 78"
						></v-text-field>

						<!-- 3. Inscrit -->
						<div class="mb-4">
							<p class="field-lbl">Client inscrit ?</p>
							<v-btn-toggle v-model="attrForm.registered" mandatory dense class="toggle-group">
								<v-btn :value="true" small class="toggle-opt">
									<v-icon left x-small>mdi-check-circle</v-icon> Oui
								</v-btn>
								<v-btn :value="false" small class="toggle-opt">
									<v-icon left x-small>mdi-close-circle</v-icon> Non
								</v-btn>
							</v-btn-toggle>
							<p v-if="attrForm.registered === false" class="attr-not-registered-info">
								<v-icon x-small color="#1565c0">mdi-information-outline</v-icon>
								Les frais d'inscription de 10 000 FCFA seront ajoutés au montant total.
							</p>
						</div>

						<!-- 4. Remise -->
						<v-text-field
							v-model.number="attrForm.discount"
							label="Remise"
							outlined dense rounded
							prepend-inner-icon="mdi-tag-minus"
							suffix="FCFA"
							type="number"
							min="0"
							:rules="attrDiscountRules"
							hide-details="auto"
							class="mb-4"
						></v-text-field>

						<!-- 5. Date -->
						<v-menu v-model="attrDateMenu" :close-on-content-click="false">
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									v-model="attrForm.date"
									label="Date d'abonnement"
									outlined dense rounded
									prepend-inner-icon="mdi-calendar"
									readonly
									:rules="[v => !!v || 'Champ requis']"
									hide-details="auto"
									class="mb-4"
									v-bind="attrs" v-on="on"
								></v-text-field>
							</template>
							<v-date-picker locale="fr" color="#c62828" v-model="attrForm.date" @input="attrDateMenu = false"></v-date-picker>
						</v-menu>

						<!-- Résumé prix -->
						<div class="price-box" v-if="attrForm.subscription">
							<div class="price-row">
								<span class="price-lbl">Prix abonnement</span>
								<span class="price-amt">{{ (attrForm.subscription.price || 0).toLocaleString('fr') }} FCFA</span>
							</div>
							<div class="price-row" v-if="attrForm.discount">
								<span class="price-lbl">Remise</span>
								<span class="price-amt price-off">− {{ (attrForm.discount || 0).toLocaleString('fr') }} FCFA</span>
							</div>

							<template v-if="attrForm.isAdvance">
								<div class="price-sep"></div>
								<div class="price-row">
									<span class="price-lbl price-total-lbl">Abonnement net</span>
									<span class="price-amt price-total-val">{{ attrSubscriptionNet.toLocaleString('fr') }} FCFA</span>
								</div>
								<div class="price-row pl-2">
									<span class="price-lbl">↳ Avance versée</span>
									<span class="price-amt price-paid-val">{{ (attrForm.amountPaid || 0).toLocaleString('fr') }} FCFA</span>
								</div>
								<div class="price-row pl-2">
									<span class="price-lbl price-remaining-lbl">↳ Reste à payer</span>
									<span class="price-amt price-remaining-val">{{ attrRemainingAmount.toLocaleString('fr') }} FCFA</span>
								</div>
							</template>
							<template v-else>
								<div class="price-sep"></div>
								<div class="price-row">
									<span class="price-lbl price-total-lbl">Abonnement net</span>
									<span class="price-amt price-total-val">{{ attrSubscriptionNet.toLocaleString('fr') }} FCFA</span>
								</div>
							</template>

							<template v-if="attrForm.registered === false">
								<div class="price-sep"></div>
								<div class="price-row">
									<span class="price-lbl price-inscription">Frais d'inscription <em style="font-style:normal;font-size:0.68rem">(soldés)</em></span>
									<span class="price-amt price-inscription">+ 10 000 FCFA</span>
								</div>
							</template>

							<template v-if="attrForm.isAdvance">
								<div class="price-sep"></div>
								<div class="price-row">
									<span class="price-lbl price-total-lbl">Encaissé aujourd'hui</span>
									<span class="price-amt price-total-val">{{ attrTodayAmount.toLocaleString('fr') }} FCFA</span>
								</div>
							</template>
							<template v-else-if="!attrForm.isAdvance && attrForm.registered === false">
								<div class="price-sep"></div>
								<div class="price-row">
									<span class="price-lbl price-total-lbl">Montant final</span>
									<span class="price-amt price-total-val">{{ attrFinalAmount.toLocaleString('fr') }} FCFA</span>
								</div>
							</template>
						</div>

						<!-- Avance -->
						<div class="advance-section mt-3">
							<v-checkbox
								v-model="attrForm.isAdvance"
								label="Le client paye une avance"
								dense hide-details
								class="mt-0 pt-0"
								color="#c62828"
							></v-checkbox>
							<v-text-field
								v-if="attrForm.isAdvance"
								v-model.number="attrForm.amountPaid"
								label="Montant versé"
								outlined dense rounded
								prepend-inner-icon="mdi-cash"
								suffix="FCFA"
								type="number"
								min="1"
								:rules="attrAmountPaidRules"
								hide-details="auto"
								class="mt-3"
							></v-text-field>
						</div>
					</v-form>
				</v-card-text>

				<v-card-actions class="pa-4 pt-2">
					<v-btn text color="error" rounded @click="closeAttribution">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn
						:disabled="!attrValid"
						depressed rounded
						class="attr-confirm-btn"
						@click="confirmAttribution"
						:loading="attrLoading"
					>
						<v-icon left small>mdi-check</v-icon>
						Confirmer
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- ─── Dialog: Attribution groupée ─── -->
		<v-dialog v-model="dialogBulk" max-width="560px" persistent scrollable>
			<v-card rounded="xl" class="pa-2">
				<div class="attr-header">
					<div class="attr-icon-box" style="background:#1565c0;">
						<v-icon size="16" color="white">mdi-account-multiple-plus</v-icon>
					</div>
					<div>
						<p class="attr-title">Attribution groupée</p>
						<p class="attr-sub">{{ bulkStep === 1 ? 'Configurer l\'attribution' : 'Récapitulatif avant validation' }}</p>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small @click="closeBulk">
						<v-icon color="#6b7280">mdi-close</v-icon>
					</v-btn>
				</div>
				<v-divider></v-divider>

				<!-- Step 1: Formulaire -->
				<v-card-text class="pt-5" v-if="bulkStep === 1">
					<v-form ref="bulkForm" v-model="bulkValid" lazy-validation>
						<v-select
							v-model="bulkForm.subscription"
							:items="subscriptions"
							return-object
							label="Abonnement"
							outlined dense rounded
							prepend-inner-icon="mdi-playlist-check"
							:rules="[v => !!v || 'Champ requis']"
							hide-details="auto"
							class="mb-4"
						>
							<template #item="{ item }">
								{{ item.label }} — {{ item.type }} — {{ item.price.toLocaleString('fr') }} FCFA
							</template>
							<template #selection="{ item }">
								{{ item.label }} — {{ item.price.toLocaleString('fr') }} FCFA
							</template>
						</v-select>

						<v-autocomplete
							v-model="bulkForm.clients"
							:items="bulkClientResults"
							:search-input.sync="bulkClientSearch"
							:filter="() => true"
							item-text="fullName"
							return-object
							multiple chips small-chips deletable-chips
							label="Clients"
							outlined dense rounded
							prepend-inner-icon="mdi-account-group"
							:rules="[v => v.length > 0 || 'Sélectionnez au moins un client']"
							hide-details="auto"
							class="mb-4"
							:loading="bulkSearchLoading"
							no-data-text="Tapez un nom pour rechercher..."
							placeholder="Rechercher des clients..."
						>
							<template #selection="{ item }">
								<v-chip small color="#eff6ff" text-color="#1565c0" close @click:close="removeClient(item)">
									{{ item.fullName }}
								</v-chip>
							</template>
						</v-autocomplete>

						<div class="mb-4">
							<p class="field-lbl">Clients inscrits ?</p>
							<v-btn-toggle v-model="bulkForm.registered" mandatory dense class="toggle-group">
								<v-btn :value="true" small class="toggle-opt">
									<v-icon left x-small>mdi-check-circle</v-icon> Oui
								</v-btn>
								<v-btn :value="false" small class="toggle-opt">
									<v-icon left x-small>mdi-close-circle</v-icon> Non
								</v-btn>
							</v-btn-toggle>
							<p v-if="bulkForm.registered === false" class="attr-not-registered-info">
								<v-icon x-small color="#1565c0">mdi-information-outline</v-icon>
								Les frais d'inscription de 10 000 FCFA seront ajoutés par client.
							</p>
						</div>

						<v-text-field
							v-model.number="bulkForm.discount"
							label="Remise par client"
							outlined dense rounded
							prepend-inner-icon="mdi-tag-minus"
							suffix="FCFA"
							type="number"
							min="0"
							:rules="bulkDiscountRules"
							hide-details="auto"
							class="mb-4"
						></v-text-field>

						<v-menu v-model="bulkDateMenu" :close-on-content-click="false">
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									v-model="bulkForm.date"
									label="Date d'abonnement"
									outlined dense rounded
									prepend-inner-icon="mdi-calendar"
									readonly
									:rules="[v => !!v || 'Champ requis']"
									hide-details="auto"
									class="mb-3"
									v-bind="attrs" v-on="on"
								></v-text-field>
							</template>
							<v-date-picker locale="fr" color="#c62828" v-model="bulkForm.date" @input="bulkDateMenu = false"></v-date-picker>
						</v-menu>

						<!-- Avance -->
						<div class="advance-section">
							<v-checkbox
								v-model="bulkForm.isAdvance"
								label="Les clients paient une avance"
								dense hide-details
								class="mt-0 pt-0"
								color="#1565c0"
							></v-checkbox>
							<v-text-field
								v-if="bulkForm.isAdvance"
								v-model.number="bulkForm.amountPaid"
								label="Montant versé par client"
								outlined dense rounded
								prepend-inner-icon="mdi-cash"
								suffix="FCFA"
								type="number"
								min="1"
								:rules="bulkAmountPaidRules"
								hide-details="auto"
								class="mt-3"
							></v-text-field>
						</div>
					</v-form>
				</v-card-text>

				<!-- Step 2: Récapitulatif -->
				<v-card-text v-else class="pt-4">
					<div class="bulk-summary">
						<div class="summary-row">
							<v-icon size="18" color="#1565c0" class="mr-2">mdi-playlist-check</v-icon>
							<div>
								<p class="summary-label">Abonnement</p>
								<p class="summary-value">{{ bulkForm.subscription ? bulkForm.subscription.label : '' }} ({{ bulkForm.subscription ? bulkForm.subscription.type : '' }})</p>
							</div>
						</div>
						<div class="summary-row">
							<v-icon size="18" color="#c62828" class="mr-2">mdi-account-group</v-icon>
							<div>
								<p class="summary-label">Clients concernés</p>
								<p class="summary-value">{{ bulkForm.clients.length }} client{{ bulkForm.clients.length > 1 ? 's' : '' }}</p>
							</div>
						</div>
						<div class="summary-row">
							<v-icon size="18" color="#9ca3af" class="mr-2">mdi-calendar</v-icon>
							<div>
								<p class="summary-label">Date</p>
								<p class="summary-value">{{ bulkForm.date }}</p>
							</div>
						</div>
						<v-divider class="my-3"></v-divider>
						<div class="summary-finances">
							<div class="summary-fin-row">
								<span>Prix unitaire</span>
								<span>{{ (bulkForm.subscription ? bulkForm.subscription.price : 0).toLocaleString('fr') }} FCFA</span>
							</div>
							<div class="summary-fin-row">
								<span>Remise / client</span>
								<span class="price-off">− {{ (bulkForm.discount || 0).toLocaleString('fr') }} FCFA</span>
							</div>
							<div class="summary-fin-row">
								<span>Abonnement net / client</span>
								<span>{{ bulkSubscriptionNet.toLocaleString('fr') }} FCFA</span>
							</div>
							<template v-if="bulkForm.isAdvance">
								<div class="summary-fin-row">
									<span>↳ Avance / client</span>
									<span class="price-paid-val">{{ (bulkForm.amountPaid || 0).toLocaleString('fr') }} FCFA</span>
								</div>
								<div class="summary-fin-row">
									<span class="price-remaining-lbl">↳ Reste / client</span>
									<span class="price-remaining-val">{{ bulkRemainingAmount.toLocaleString('fr') }} FCFA</span>
								</div>
							</template>
							<div class="summary-fin-row" v-if="bulkForm.registered === false">
								<span>Inscription / client <em style="font-style:normal;font-size:0.72rem;color:#9ca3af">(soldée)</em></span>
								<span class="price-inscription">+ 10 000 FCFA</span>
							</div>
							<div class="summary-fin-row summary-total">
								<span>Total encaissé aujourd'hui</span>
								<span>{{ (bulkForm.isAdvance ? bulkTodayAmount : bulkTotalRevenue).toLocaleString('fr') }} FCFA</span>
							</div>
						</div>
					</div>
				</v-card-text>

				<v-card-actions class="pa-4 pt-2">
					<v-btn text color="grey" rounded @click="bulkStep === 1 ? closeBulk() : (bulkStep = 1)">
						{{ bulkStep === 1 ? 'Annuler' : 'Retour' }}
					</v-btn>
					<v-spacer></v-spacer>
					<v-btn
						v-if="bulkStep === 1"
						:disabled="!bulkValid || bulkForm.clients.length === 0"
						depressed rounded
						class="attr-confirm-btn"
						@click="bulkStep = 2"
					>
						Suivant <v-icon right small>mdi-arrow-right</v-icon>
					</v-btn>
					<v-btn
						v-else
						depressed rounded
						class="attr-confirm-btn"
						@click="confirmBulk"
						:loading="bulkLoading"
					>
						<v-icon left small>mdi-check</v-icon>
						Confirmer ({{ bulkForm.clients.length }} clients)
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- ─── Dialog: Suppression ─── -->
		<v-dialog v-model="dialogDelete" max-width="440px">
			<v-card rounded="xl" class="pa-2">
				<div class="del-header">
					<div class="del-icon"><v-icon size="18" color="white">mdi-alert</v-icon></div>
					<p class="del-title">Supprimer cet abonnement ?</p>
				</div>
				<v-card-actions class="pa-4 pt-2">
					<v-btn text color="grey" rounded @click="dialogDelete = false">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn depressed rounded class="del-confirm-btn" @click="deleteItemConfirm">Supprimer</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</section>
</template>

<script>
import Loader from "@/components/Loader.vue";
import service from "../services";
import FloatingAlert from "@/components/FloatingAlert.vue";
export default {
	components: { Loader, FloatingAlert },
	data: () => ({
		search: "",
		searchTimeout: null,
		accessDenied: false,
		showAlert: false,
		showAlertMessage: "",
		showAlertColor: "",
		dialog: false,
		dialogDelete: false,
		loaderDialog: false,
		loading: false,
		options: {},
		totalItems: 0,
		headers: [
			{ text: "Intitulé", value: "label", sortable: false },
			{ text: "Prix", value: "price", sortable: false },
			{ text: "Durée", value: "duration", sortable: false },
			{ text: "Type", value: "type", sortable: false },
			{ text: "Actions", value: "actions", sortable: false },
		],
		attrClientSearch: "",
		attrClientResults: [],
		attrSearchLoading: false,
		attrSearchTimeout: null,
		bulkClientSearch: "",
		bulkClientResults: [],
		bulkSearchLoading: false,
		bulkSearchTimeout: null,
		subscriptions: [],
		subscriptionPeriod: {},
		subscriptionTypes: [
			{ type: "Classique", period: [{ label: "Mensuel", price: 10000, duration: 30 }, { label: "Trimestriel", price: 25000, duration: 90 }, { label: "Semestriel", price: 45000, duration: 180 }, { label: "Annuel", price: 85000, duration: 360 }] },
			{ type: "Selfworkout", period: [{ label: "Mensuel", price: 15000, duration: 30 }, { label: "Trimestriel", price: 40000, duration: 90 }, { label: "Semestriel", price: 75000, duration: 180 }, { label: "Annuel", price: 150000, duration: 360 }] },
			{ type: "Build muscle", period: [{ label: "Mensuel", price: 25000, duration: 30 }, { label: "Trimestriel", price: 65000, duration: 90 }, { label: "Semestriel", price: 125000, duration: 180 }, { label: "Annuel", price: 240000, duration: 360 }] },
			{ type: "Premium", period: [{ label: "Mensuel", price: 80000, duration: 30 }, { label: "Trimestriel", price: 220000, duration: 90 }, { label: "Semestriel", price: 420000, duration: 180 }, { label: "Annuel", price: 810000, duration: 360 }] },
		],
		editedIndex: -1,
		modifiedSubscriptionId: "",
		editedItem: { label: "", price: 0, duration: 0, type: "", _id: "" },
		defaultItem: { label: "", price: 0, duration: 0, type: "", _id: "" },

		// Attribution individuelle
		dialogAttribution: false,
		attrDateMenu: false,
		attrValid: false,
		attrLoading: false,
		attrForm: { subscription: null, client: null, registered: true, discount: 0, date: "", isAdvance: false, amountPaid: 0, newClientPhone: "" },

		// Attribution groupée
		dialogBulk: false,
		bulkStep: 1,
		bulkDateMenu: false,
		bulkValid: false,
		bulkLoading: false,
		bulkForm: { subscription: null, clients: [], registered: true, discount: 0, date: "", isAdvance: false, amountPaid: 0 },
	}),

	computed: {
		formTitle() { return this.editedIndex === -1 ? "Nouvel abonnement" : "Modifier l'abonnement"; },

		attrSubscriptionNet() {
			if (!this.attrForm.subscription) return 0;
			return Math.max(0, (this.attrForm.subscription.price || 0) - (this.attrForm.discount || 0));
		},
		attrInscriptionFee() {
			return this.attrForm.registered === false ? 10000 : 0;
		},
		attrFinalAmount() {
			return this.attrSubscriptionNet + this.attrInscriptionFee;
		},
		attrDiscountRules() {
			return [
				v => v >= 0 || "La remise ne peut pas être négative",
				v => !this.attrForm.subscription || v <= this.attrForm.subscription.price || "La remise dépasse le prix de l'abonnement",
			];
		},
		attrRemainingAmount() {
			return Math.max(0, this.attrSubscriptionNet - (this.attrForm.amountPaid || 0));
		},
		attrTodayAmount() {
			return (this.attrForm.amountPaid || 0) + this.attrInscriptionFee;
		},
		attrAmountPaidRules() {
			return [
				v => !this.attrForm.isAdvance || (v !== null && v !== '' && Number(v) > 0) || "Le montant versé doit être positif",
				v => !this.attrForm.isAdvance || Number(v) < this.attrSubscriptionNet || "Doit être inférieur au montant de l'abonnement",
			];
		},

		bulkSubscriptionNet() {
			if (!this.bulkForm.subscription) return 0;
			return Math.max(0, (this.bulkForm.subscription.price || 0) - (this.bulkForm.discount || 0));
		},
		bulkInscriptionFee() {
			return this.bulkForm.registered === false ? 10000 : 0;
		},
		bulkFinalAmount() {
			return this.bulkSubscriptionNet + this.bulkInscriptionFee;
		},
		bulkDiscountRules() {
			return [
				v => v >= 0 || "La remise ne peut pas être négative",
				v => !this.bulkForm.subscription || v <= this.bulkForm.subscription.price || "La remise dépasse le prix de l'abonnement",
			];
		},
		bulkRemainingAmount() {
			return Math.max(0, this.bulkSubscriptionNet - (this.bulkForm.amountPaid || 0));
		},
		bulkAmountPaidRules() {
			return [
				v => !this.bulkForm.isAdvance || (v !== null && v !== '' && Number(v) > 0) || "Le montant versé doit être positif",
				v => !this.bulkForm.isAdvance || Number(v) < this.bulkSubscriptionNet || "Doit être inférieur au montant de l'abonnement",
			];
		},
		bulkTotalRevenue() {
			return this.bulkFinalAmount * this.bulkForm.clients.length;
		},
		bulkTodayAmount() {
			return ((this.bulkForm.amountPaid || 0) + this.bulkInscriptionFee) * this.bulkForm.clients.length;
		},
	},

	watch: {
		options: {
			handler() { this.initialize(); },
			deep: true,
		},
		search() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				if (this.options.page !== 1) this.options.page = 1;
				else this.initialize();
			}, 400);
		},
		dialog(val) { val || this.close(); },
		dialogDelete(val) { val || this.closeDelete(); },
		attrClientSearch(val) {
			if (!val || val.trim().length < 2) return;
			clearTimeout(this.attrSearchTimeout);
			this.attrSearchTimeout = setTimeout(() => this.fetchClients(val, 'attr'), 350);
		},
		bulkClientSearch(val) {
			if (!val || val.trim().length < 2) return;
			clearTimeout(this.bulkSearchTimeout);
			this.bulkSearchTimeout = setTimeout(() => this.fetchClients(val, 'bulk'), 350);
		},
	},

	methods: {
		initialize() {
			this.loading = true;
			const page = this.options.page || 1;
			const limit = this.options.itemsPerPage || 10;
			service.getSubscriptionsPaginated({ page, limit, search: this.search })
				.then((r) => { this.subscriptions = r.data.subscriptions; this.totalItems = r.data.totalPages * limit; this.loading = false; })
				.catch((e) => { console.log(e); this.accessDenied = !!e; this.loading = false; });
		},

		manageAlert(show, type, message) {
			this.showAlert = show;
			this.showAlertColor = type;
			this.showAlertMessage = message;
		},

		fetchClients(query, context) {
			if (context === 'attr') {
				this.attrSearchLoading = true;
				service.searchClients(query)
					.then((r) => {
						const customers = r.data?.customers || [];
						if (customers.length === 0) {
							this.attrClientResults = [{ _id: null, fullName: query.trim(), isNew: true }];
						} else {
							this.attrClientResults = customers;
						}
						this.attrSearchLoading = false;
					})
					.catch(() => { this.attrSearchLoading = false; });
			} else {
				this.bulkSearchLoading = true;
				service.searchClients(query)
					.then((r) => {
						const fresh = r.data?.customers || [];
						const selected = this.bulkForm.clients || [];
						const selectedIds = new Set(selected.map(c => c._id));
						this.bulkClientResults = [
							...selected,
							...fresh.filter(c => !selectedIds.has(c._id)),
						];
						this.bulkSearchLoading = false;
					})
					.catch(() => { this.bulkSearchLoading = false; });
			}
		},

		// ─── Attribution individuelle ───
		openAttribution(subscription) {
			this.attrForm = { subscription: subscription || null, client: null, registered: true, discount: 0, date: "", isAdvance: false, amountPaid: 0, newClientPhone: "" };
			this.attrClientResults = [];
			this.attrClientSearch = "";
			this.dialogAttribution = true;
		},
		closeAttribution() {
			this.dialogAttribution = false;
			this.$nextTick(() => {
				this.attrForm = { subscription: null, client: null, registered: true, discount: 0, date: "", isAdvance: false, amountPaid: 0, newClientPhone: "" };
				if (this.$refs.attrForm) this.$refs.attrForm.resetValidation();
			});
		},
		confirmAttribution() {
			if (!this.$refs.attrForm.validate()) return;
			this.attrLoading = true;

			const doAttribution = (customerId) => {
				service.attributionSubscriptionEnhanced(
					this.attrForm.subscription._id,
					customerId,
					this.attrForm.date,
					this.attrForm.registered,
					this.attrForm.discount || 0,
					this.attrForm.isAdvance ? (this.attrForm.amountPaid || 0) : null
				).then(() => {
					this.attrLoading = false;
					this.manageAlert(true, "success", "Abonnement attribué avec succès !");
					this.closeAttribution();
				}).catch((e) => {
					this.attrLoading = false;
					this.manageAlert(true, "error", e?.message || "Erreur lors de l'attribution");
				});
			};

			if (this.attrForm.client.isNew) {
				service.createQuickCustomer(
					this.attrForm.client.fullName,
					this.attrForm.newClientPhone || null
				).then((r) => {
					doAttribution(r.data.data.customerId);
				}).catch((e) => {
					this.attrLoading = false;
					this.manageAlert(true, "error", e?.data?.message || "Erreur lors de la création du client");
				});
			} else {
				doAttribution(this.attrForm.client._id);
			}
		},

		// ─── Attribution groupée ───
		openBulk() {
			this.bulkForm = { subscription: null, clients: [], registered: true, discount: 0, date: "", isAdvance: false, amountPaid: 0 };
			this.bulkStep = 1;
			this.bulkClientResults = [];
			this.bulkClientSearch = "";
			this.dialogBulk = true;
		},
		closeBulk() {
			this.dialogBulk = false;
			this.bulkStep = 1;
			this.$nextTick(() => {
				this.bulkForm = { subscription: null, clients: [], registered: true, discount: 0, date: "", isAdvance: false, amountPaid: 0 };
				if (this.$refs.bulkForm) this.$refs.bulkForm.resetValidation();
			});
		},
		removeClient(client) {
			this.bulkForm.clients = this.bulkForm.clients.filter(c => c._id !== client._id);
		},
		confirmBulk() {
			this.bulkLoading = true;
			const customerIds = this.bulkForm.clients.map(c => c._id);
			service.attributionSubscriptionBulk(
				this.bulkForm.subscription._id,
				customerIds,
				this.bulkForm.date,
				this.bulkForm.registered,
				this.bulkForm.discount || 0,
				this.bulkForm.isAdvance ? (this.bulkForm.amountPaid || 0) : null
			).then(() => {
				this.bulkLoading = false;
				this.manageAlert(true, "success", `${customerIds.length} attribution${customerIds.length > 1 ? 's' : ''} effectuée${customerIds.length > 1 ? 's' : ''} !`);
				this.closeBulk();
			}).catch((e) => {
				this.bulkLoading = false;
				this.manageAlert(true, "error", e?.message || "Erreur lors de l'attribution groupée");
			});
		},

		// ─── CRUD abonnements ───
		editItem(item) { this.editedIndex = this.subscriptions.indexOf(item); this.editedItem = Object.assign({}, item); this.modifiedSubscriptionId = item._id; this.dialog = true; },
		deleteItem(item) { this.editedIndex = this.subscriptions.indexOf(item); this.editedItem = Object.assign({}, item); this.dialogDelete = true; },
		deleteItemConfirm() {
			this.showAlert = false;
			this.loaderDialog = true;
			service.deleteSubscription(this.editedItem._id)
				.then(() => { this.loaderDialog = false; this.closeDelete(); })
				.catch((e) => { this.manageAlert(true, "error", e?.message || "Suppression impossible — abonnement attribué à un client."); this.loaderDialog = false; });
		},
		close() {
			this.dialog = false;
			this.$nextTick(() => { this.editedItem = Object.assign({}, this.defaultItem); this.initialize(); this.editedIndex = -1; });
		},
		closeDelete() {
			this.dialogDelete = false;
			this.$nextTick(() => { this.initialize(); this.editedIndex = -1; });
		},
		save() {
			this.showAlert = false;
			if (this.editedIndex > -1) {
				this.editedItem.type = this.subscriptionPeriod.type;
				this.editedItem._id = this.modifiedSubscriptionId;
				this.loaderDialog = true;
				service.updateSubscription(this.editedItem._id, this.editedItem)
					.then(() => { this.loaderDialog = false; this.close(); })
					.catch((e) => console.log(e));
			} else {
				this.loaderDialog = true;
				this.editedItem.type = this.subscriptionPeriod.type;
				service.createSubscription(this.editedItem)
					.then(() => { this.loaderDialog = false; this.close(); })
					.catch((e) => { console.log(e); this.loaderDialog = false; this.manageAlert(true, "error", e?.message || "Vérifiez votre connexion internet !"); });
			}
		},
	},
};
</script>

<style scoped>
.sub-header { display: flex; align-items: center; padding: 18px 20px 14px; gap: 14px; flex-wrap: wrap; }
.sub-header-left { display: flex; align-items: center; gap: 14px; flex: 1; min-width: 200px; }
.sub-icon-box { width: 38px; height: 38px; border-radius: 10px; background: #1565c0; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.sub-title { font-size: 0.92rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.sub-subtitle { font-size: 0.72rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.sub-search { max-width: 240px; }
.sub-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.sub-add-btn { background: #c62828 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
.sub-attr-btn { background: #1565c0 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
.sub-bulk-btn { background: #111827 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
.sub-table { font-family: Montserrat, sans-serif; }
.price-value { font-weight: 700; color: #00695c; }
.sub-dialog-header { display: flex; align-items: center; gap: 14px; padding: 18px 20px 14px; }
.sub-icon-box-sm { width: 34px; height: 34px; border-radius: 8px; background: #1565c0; display: flex; align-items: center; justify-content: center; }
.sub-dialog-title { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.sub-dialog-sub { font-size: 0.68rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.sub-save-btn { background: #1565c0 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }

/* Attribution */
.attr-header { display: flex; align-items: center; gap: 14px; padding: 18px 20px 14px; }
.attr-icon-box { width: 34px; height: 34px; border-radius: 8px; background: #c62828; display: flex; align-items: center; justify-content: center; }
.attr-title { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.attr-sub { font-size: 0.68rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.attr-confirm-btn { background: #c62828 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
.field-lbl { font-size: 0.75rem; font-weight: 600; color: #6b7280; font-family: Montserrat, sans-serif; margin: 0 0 8px; }

/* Toggle */
.toggle-group .toggle-opt { font-family: Montserrat, sans-serif; font-size: 0.78rem; font-weight: 600; text-transform: none; letter-spacing: 0; }
.attr-not-registered-info { font-size: 0.72rem; color: #1565c0; font-family: Montserrat, sans-serif; margin: 6px 0 0; display: flex; align-items: center; gap: 4px; }

/* Résumé prix */
.price-box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 12px; padding: 14px 16px; }
.price-row { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; }
.price-lbl { font-size: 0.78rem; color: #6b7280; font-family: Montserrat, sans-serif; }
.price-amt { font-size: 0.82rem; font-weight: 600; color: #111827; font-family: Montserrat, sans-serif; }
.price-off { color: #c62828; }
.price-inscription { color: #1565c0; }
.price-sep { height: 1px; background: #e5e7eb; margin: 8px 0; }
.price-total-lbl { font-weight: 700; color: #111827; }
.price-total-val { font-size: 0.92rem; font-weight: 800; color: #00695c; }
.price-paid-val { color: #1565c0; font-weight: 700; }
.price-remaining-lbl { font-weight: 600; color: #b45309; }
.price-remaining-val { color: #b45309; font-weight: 700; }
.advance-section { border-top: 1px dashed #e5e7eb; padding-top: 12px; margin-top: 4px; }

/* Résumé bulk */
.bulk-summary { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px; }
.summary-row { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
.summary-label { font-size: 0.68rem; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.summary-value { font-size: 0.85rem; font-weight: 700; color: #111827; margin: 0; font-family: Montserrat, sans-serif; }
.summary-finances { }
.summary-fin-row { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; font-size: 0.82rem; font-family: Montserrat, sans-serif; color: #374151; border-bottom: 1px solid #f3f4f6; }
.summary-fin-row:last-child { border-bottom: none; }
.summary-total { font-weight: 800; color: #00695c; font-size: 0.92rem; padding-top: 10px; margin-top: 4px; }

/* Delete */
.del-header { display: flex; align-items: center; gap: 12px; padding: 16px 20px 12px; }
.del-icon { width: 34px; height: 34px; border-radius: 8px; background: #c62828; display: flex; align-items: center; justify-content: center; }
.del-title { font-size: 0.9rem; font-weight: 700; color: #111827; margin: 0; font-family: Montserrat, sans-serif; }
.del-confirm-btn { background: #c62828 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
</style>

